/** Ask - a place of the menu (ADR-0212 decisions 1 and 7) that is a fixed list of questions and an
 * honest "AI is off", and nothing more.
 *
 * - **Ask offers no way to ask.** ADR-0011 confines any model to synthetic and sample documents
 *   until the DPA, the customer notice and the transfer assessment exist, and those need counsel
 *   (B-02). So the page says AI is off for the workspace and lists the questions a person most often
 *   brings, each of which **opens the screen that answers it from the person's own figures** - no
 *   model, no request. There is no input box: a text field that goes nowhere would be a control that
 *   predictably does nothing, and a canned reply would be a fabricated answer. No request leaves
 *   this page; it makes no API call of its own, which is asserted in `Stage.test.tsx`.
 * - **The way to turn it on is an Admin's** (ADR-0192): the link to the assistant settings is drawn
 *   for an Admin and for nobody else, so no one is sent to a page that refuses them. Why AI is off,
 *   and what turning it on depends on, are behind the info control (`infoText.ts`, `ask.off`).
 * - **Dashboards** moved to `Dashboards.tsx` at F5 (four fixed views, ADR-0195). **Support** moved
 *   to `Support.tsx` at F4.
 */

import { Sparkle } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";

import { useAssistantStatus } from "../api/assistant";
import type { AssistantStatus } from "../api/assistant";
import { useMe } from "../api/auth";
import { ReadsFailed } from "../components/Loaded";
import { GridTable, InfoPopover, StatusChip, TablePage } from "../components/ui";
import type { GridColumn } from "../components/ui";
import { RequireSession } from "../shell/RequireSession";
import { keepSearch } from "./search";
import styles from "./Screen.module.css";
import { ScreenFrame } from "./ScreenFrame";
import { useScreen } from "./useScreen";

interface Question {
  id: string;
  text: string;
  to: string;
  where: string;
  tab?: string;
}

/** The questions, and the screen that answers each from the person's own figures. A question whose
 * screen does not exist yet is not listed: this is not a menu of what the product might one day say. */
const QUESTIONS: readonly Question[] = [
  { id: "total", text: "How much did we emit this period?", to: "/", where: "Home" },
  { id: "largest", text: "Which site emits the most?", to: "/scope-1", where: "Scope 1" },
  { id: "electricity", text: "What did purchased electricity come to?", to: "/scope-2", where: "Scope 2" },
  { id: "missing", text: "Which bills have not arrived?", to: "/processing", where: "Processing", tab: "missing" },
  { id: "review", text: "What is waiting for me?", to: "/approvals", where: "Action items" },
  { id: "due", text: "What is due, and how close is it?", to: "/reports", where: "Filings" },
  { id: "approved", text: "Who approved a figure?", to: "/ledger", where: "Audit ledger" },
];

/** The first condition that keeps the assistant off, as a short label for a chip. The *sentence* is the
 * server's and rides on the chip as its title (`AssistantStatusOut.message`, written once there: design
 * rule 7); this is only the label, keyed on the server's code, so the chip says which of the three
 * conditions fails without a paragraph on a screen that is held to a word budget. An unknown code
 * falls back to a label that claims nothing. */
const REASON_LABEL: Readonly<Record<string, string>> = {
  assistant_not_entitled: "Not in your agreement",
  assistant_suspended_by_operator: "Switched off by EmitIQ",
  assistant_not_connected: "No provider connected",
  assistant_switched_off: "Switched off by an Admin",
  assistant_role_not_permitted: "Not open to your role",
  assistant_rates_missing: "Setup not finished",
  assistant_cap_undecided: "Setup not finished",
};

function WhyOff({ status }: { status: ReturnType<typeof useAssistantStatus> }) {
  // Pending or failed: no chip. A failed read is said by the page (`ReadsFailed` below), never by a
  // reason it did not get; the chip beside it still says AI is off, which is true whatever the answer
  // because this page offers no way to ask.
  if (status.isPending || status.error || !status.data) return null;
  const data: AssistantStatus = status.data;
  if (data.on) return null;
  return (
    <StatusChip
      tone="warn"
      size="sm"
      testId="ask-why-off"
      title={data.message ?? undefined}
    >
      {(data.code ? REASON_LABEL[data.code] : undefined) ?? "Not available yet"}
    </StatusChip>
  );
}

export function AskScreen() {
  return (
    <RequireSession>
      <AskBody />
    </RequireSession>
  );
}

function AskBody() {
  const screen = useScreen();
  const { data: me } = useMe();
  const status = useAssistantStatus();
  const carry = keepSearch(screen);
  const columns: GridColumn<Question>[] = [
    { id: "q", header: "Question", cell: (row) => row.text },
    {
      id: "where",
      header: "Answered on",
      cell: (row) => (
        <Link to={row.to} search={{ ...carry, ...(row.tab ? { tab: row.tab } : {}) }} data-testid={`ask-${row.id}`}>
          {row.where}
        </Link>
      ),
    },
  ];
  return (
    <ScreenFrame screen={screen} title="Ask" crumb="questions in plain words, answered from your figures">
      <div className={styles.passThrough} data-testid="ask-ai-off">
        <TablePage
          icon={Sparkle}
          title="Ask"
          label="Questions"
          actions={
            <span>
              <StatusChip tone="neutral" size="sm">
                AI is off
              </StatusChip>
              <WhyOff status={status} />
              <InfoPopover infoKey="ask.off" />
              {me?.role === "admin" ? (
                <>
                  {" "}
                  <Link to="/settings/ai" search={carry} data-testid="ask-turn-on">
                    AI assistant settings
                  </Link>
                </>
              ) : null}
            </span>
          }
          extra={
            <ReadsFailed reads={[{ of: "Why AI is off", read: status }]} testId="ask-status-failed" />
          }
          table={
            <GridTable
              caption="Questions and the screens that answer them"
              columns={columns}
              rows={QUESTIONS}
              rowKey={(row) => row.id}
              rowNumbers={false}
              testId="ask-questions"
            />
          }
        />
      </div>
    </ScreenFrame>
  );
}
